import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';

const F = { fontFamily: 'Inter' } as const;

/**
 * Standart "Profili Düzenle" görünümü — öğrenci ve öğretmen profillerinde
 * ortak (T16 devamı: iki rol arasındaki düzen tutarsızlığını kapatır).
 * İki eşit genişlikte kart: "Bilgilerim" (Ad/Soyad/E-posta/Telefon düzenlenebilir
 * + role özel salt-okunur alanlar) ve kimlik sağlayıcısının şifre yönlendirmesi.
 * Kaydet sonrası AuthContext.refresh() ile /auth/me yeniden çekilir, ayrıca
 * localStorage'daki cached user senkronlanır.
 */
const ProfileEditView: React.FC<{
  onBack: () => void;
  /** Role özel değiştirilemez alanlar (öğrenci: Okul/Sınıf/Kitap Kodu; öğretmen: Okul/Branş/Seviye). */
  readOnlyRows: { label: string; value: string }[];
  /** Form ön-dolumu için kaynak user (sayfa `fresh ?? user` geçirebilir). Verilmezse context user. */
  source?: any;
  /** Başarılı bilgi kaydından sonra PATCH yanıtıyla çağrılır (ör. sayfanın `fresh` state'ini güncellemek için). */
  onSaved?: (freshUser: any) => void;
  /** AP62: veli profili için Meslek alanını da düzenlenebilir yap (backend updateMe occupation'ı destekliyor). */
  showOccupation?: boolean;
}> = ({ onBack, readOnlyRows, source, onSaved, showOccupation }) => {
  const { user, refresh } = useAuth();
  const src: any = source ?? user;

  // Identity is owned by Birey Kimlik for this account (/auth/me
  // `is_birey_managed`, derived from users.birey_user_id).
  //   * Ad/Soyad/E-posta/Telefon are the IdP's. PATCH /auth/me now refuses a real
  //     change to them (auth.service.ts), and the identity feed overwrites
  //     first/last name on the next user.* event anyway — so an editable input
  //     here is a form that silently loses. The admin panel already locks the
  //     same four fields; this closes the self-service side.
  const idpManaged = Boolean(src?.is_birey_managed);

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', occupation: '' });
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoMsg, setInfoMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    const name = src?.name ? String(src.name) : '';
    const surname = src?.surname ?? (name.split(' ').slice(-1)[0] ?? '');
    const firstName = surname && name.endsWith(surname)
      ? name.slice(0, name.length - surname.length).trim() || name
      : name;
    setForm({
      firstName,
      lastName: surname,
      email: src?.email ?? '',
      phone: src?.phone ?? '',
      occupation: src?.parent_occupation ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveInfo = async () => {
    // Only validate identity when this form actually owns it. For an IdP-managed
    // account the fields are locked and their values come from Birey Kimlik, so
    // rejecting the save because one of them looks empty would block the one
    // field the person CAN edit (Meslek) over data they cannot touch.
    if (!idpManaged && (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim())) {
      setInfoMsg({ kind: 'err', text: 'Ad, soyad ve e-posta zorunlu.' });
      return;
    }
    setInfoSaving(true);
    setInfoMsg(null);
    try {
      // Send only what is editable. Echoing the locked identity fields back was
      // not harmless: the form re-derives firstName/lastName by SPLITTING the
      // joined `name`, and ~28% of stored names carry edge whitespace, so the
      // values sent back differ from the stored bytes and the server's lock read
      // them as an attempted change — a veli saving only Meslek got a 403 naming
      // four fields they cannot edit, and Meslek was never written. Omitting the
      // keys makes the server skip both the lock check and the writes.
      const r = await authApi.updateMe({
        ...(idpManaged ? {} : {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
        }),
        ...(showOccupation ? { occupation: form.occupation.trim() || null } : {}),
      });
      // localStorage'daki cached user'ı da güncelle ki sayfa yenilense bile yeni veriler görünsün
      try {
        const cached = JSON.parse(localStorage.getItem('dijipo_user') ?? 'null');
        if (cached) localStorage.setItem('dijipo_user', JSON.stringify({ ...cached, ...r.data }));
      } catch { /* ignore */ }
      await refresh().catch(() => { /* context refresh best-effort */ });
      onSaved?.(r.data);
      setInfoMsg({ kind: 'ok', text: 'Bilgiler güncellendi.' });
    } catch (e: any) {
      setInfoMsg({ kind: 'err', text: e?.response?.data?.error ?? 'Güncellenemedi' });
    } finally {
      setInfoSaving(false);
    }
  };

  const labelStyle = { ...F, fontSize: 13, fontWeight: 600, color: '#3973bf', display: 'block', marginBottom: 4 } as const;
  const inputClass = 'w-full bg-white rounded-[10px] px-3 py-2 outline-none text-[14px] text-[#2b2b2b]';
  const inputStyle = { border: '1.5px solid #d4d4d4', ...F } as const;
  const lockedInputStyle = { border: '1.5px solid #e6e6e6', background: '#f4f4f4', color: '#888', cursor: 'not-allowed', ...F } as const;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack}
          className="w-[40px] h-[40px] rounded-full bg-white flex items-center justify-center hover:bg-gray-50 transition-colors"
          style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2" strokeLinecap="round"><path d="M13 4l-6 6 6 6" /></svg>
        </button>
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Profili Düzenle</h1>
      </div>

      <div className="max-w-[1100px] space-y-5">
        {/* Bilgilerim */}
        <div className="bg-white rounded-[24px] p-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <h2 className="mb-5" style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Bilgilerim</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-[760px]">
            <label className="block">
              <span style={labelStyle}>Ad</span>
              <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                readOnly={idpManaged} disabled={idpManaged}
                className={inputClass} style={idpManaged ? lockedInputStyle : inputStyle} />
            </label>
            <label className="block">
              <span style={labelStyle}>Soyad</span>
              <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                readOnly={idpManaged} disabled={idpManaged}
                className={inputClass} style={idpManaged ? lockedInputStyle : inputStyle} />
            </label>
            <label className="block">
              <span style={labelStyle}>E-posta</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                readOnly={idpManaged} disabled={idpManaged}
                className={inputClass} style={idpManaged ? lockedInputStyle : inputStyle} />
            </label>
            <label className="block">
              <span style={labelStyle}>Telefon</span>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                readOnly={idpManaged} disabled={idpManaged}
                className={inputClass} style={idpManaged ? lockedInputStyle : inputStyle} />
            </label>
            {showOccupation && (
              <label className="block">
                <span style={labelStyle}>Meslek</span>
                <input value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                  className={inputClass} style={inputStyle} placeholder="—" />
              </label>
            )}
          </div>

          {readOnlyRows.length > 0 && (
            <div className="mt-5 pt-5 border-t border-[#eeeeee] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-4 max-w-[760px]">
              {readOnlyRows.map((r, i) => (
                <div key={i} className="min-w-0">
                  <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#888', marginBottom: 4 }}>{r.label}</p>
                  <p style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }} className="break-words">{r.value}</p>
                </div>
              ))}
            </div>
          )}

          {idpManaged && (
            <p className="mt-5" style={{ ...F, fontSize: 13, color: '#888', lineHeight: 1.5 }}>
              Ad, soyad, e-posta ve telefon bilgileriniz Birey Kimlik tarafından yönetilir ve buradan
              değiştirilemez. Güncellemek için kurum yöneticinizle görüşün.
            </p>
          )}

          {/* With every identity field locked, a "Bilgileri Kaydet" button has
              nothing left to save unless Meslek is on the form (veli). */}
          {(!idpManaged || showOccupation) && (
          <div className="flex items-center justify-end gap-4 mt-6">
            {infoMsg && (
              <span className={`text-[13px] ${infoMsg.kind === 'ok' ? 'text-green-600' : 'text-red-500'}`} style={F}>
                {infoMsg.text}
              </span>
            )}
            <button onClick={handleSaveInfo} disabled={infoSaving}
              className="rounded-[12px] px-6 py-2.5 hover:opacity-90 disabled:opacity-50"
              style={{ background: '#3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <span style={{ ...F, fontSize: 14, fontWeight: 700, color: '#fafafa' }}>
                {infoSaving ? 'Kaydediliyor…' : 'Bilgileri Kaydet'}
              </span>
            </button>
          </div>
          )}
        </div>

        <div className="bg-white rounded-[24px] p-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <h2 className="mb-3" style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Şifre</h2>
          <p style={{ ...F, fontSize: 14, color: '#5b6472', lineHeight: 1.6 }}>
            bryekosis LMS'ye Birey Kimlik hesabınızla giriş yapıyorsunuz. Şifrenizi değiştirmek için
            Birey Kimlik hesap ayarlarınızı kullanın.
          </p>
        </div>

      </div>
    </div>
  );
};

export default ProfileEditView;
